/**
 * Import attendance from a register — TRUS-562, the third recording surface.
 *
 * "the user, admin or an uploaded list will serve as a means to record who
 * attended each training."
 *
 * Two steps, always. Picking a file runs a DRY RUN and shows what would
 * happen; a second, separate click is what keeps any of it. The server executes
 * every row for real and rolls back, so the preview is an outcome rather than a
 * prediction — and these rows are evidence under a retention obligation that
 * began 1/1/2026, which is not a thing to write on a single click.
 *
 * The failures list is the useful half of the preview. A register is a
 * spreadsheet somebody else maintained: a leaver who is no longer staff, a
 * training renamed last quarter, a date typed as 01/02/26. Each one comes back
 * with its LINE NUMBER and what was wrong, because the alternative is an admin
 * re-reading 200 rows to find the one bad cell.
 */

import React, { useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Upload } from 'lucide-react'
import { Button, ErrorNote, Modal, Pill } from './ui'
import { AttendanceImportResult, importAttendance } from './api'

export function AttendanceImportDialog({
  open,
  onClose,
  onImported,
}: {
  open: boolean
  onClose: () => void
  onImported?: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<AttendanceImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setFile(null)
    setPreview(null)
    setError(null)
    setBusy(false)
    if (inputRef.current) inputRef.current.value = ''
  }

  const close = () => {
    reset()
    onClose()
  }

  const choose = async (picked: File | null) => {
    setFile(picked)
    setPreview(null)
    setError(null)
    if (!picked) return
    setBusy(true)
    try {
      setPreview(await importAttendance(picked, true))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read that file.')
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const result = await importAttendance(file, false)
      setPreview(result)
      onImported?.()
      // Leave the result on screen rather than closing: the admin still needs
      // to see which rows did not make it.
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not import the file.')
    } finally {
      setBusy(false)
    }
  }

  const done = preview !== null && preview.dry_run === false

  return (
    <Modal
      open={open}
      wide
      title="Import attendance"
      description={
        done
          ? undefined
          : 'Upload the register as a CSV. Nothing is saved until you have seen what it would do.'
      }
      onClose={close}
      footer={
        done ? (
          <Button variant="primary" onClick={close}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void confirm()}
              disabled={busy || !preview || preview.created === 0}
            >
              <Upload className="h-4 w-4" />
              {busy
                ? 'Working…'
                : preview
                  ? `Import ${preview.created} record${preview.created === 1 ? '' : 's'}`
                  : 'Import'}
            </Button>
          </>
        )
      }
    >
      {error && <ErrorNote message={error} onDismiss={() => setError(null)} />}

      <div className="space-y-4">
        {!done && (
          <div>
            <input
              ref={inputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => void choose(e.target.files?.[0] ?? null)}
              className="block w-full text-[13px] text-ink file:mr-3 file:rounded-md file:border file:border-v2border file:bg-surface file:px-3 file:py-1.5 file:text-[13px] file:font-medium file:text-ink hover:file:bg-paper-2"
            />
            <p className="mt-2 text-[12px] text-ink-400">
              Needs an <strong>email</strong> column and a <strong>training</strong> column (its title, or
              <strong> training_id</strong>), plus the completion <strong>date</strong>. Dates need a four-digit
              year. Hours, notes, provider and certificate number are optional.
            </p>
          </div>
        )}

        {busy && !preview && (
          <div className="py-4 text-center text-[13px] text-ink-400">Checking the file…</div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[13px]">
              <FileSpreadsheet className="h-4 w-4 text-ink-400" />
              <span className="text-ink">{preview.filename}</span>
              {preview.created > 0 && (
                <Pill tone="success">
                  <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" />
                  {preview.created} {done ? 'recorded' : 'to record'}
                </Pill>
              )}
              {preview.already_recorded > 0 && (
                <Pill>{preview.already_recorded} already on file</Pill>
              )}
              {preview.failed > 0 && (
                <Pill tone="danger">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  {preview.failed} cannot be read
                </Pill>
              )}
            </div>

            {preview.created === 0 && preview.failed === 0 && preview.already_recorded > 0 && (
              <p className="text-[13px] text-ink-500">
                Every row in this file is already recorded. Nothing further to do.
              </p>
            )}

            {preview.failed > 0 && (
              <div>
                <p className="mb-1 text-[13px] font-medium text-ink">Rows that cannot be imported</p>
                <ul className="max-h-48 overflow-y-auto rounded-md border border-v2border divide-y divide-v2border text-[13px]">
                  {preview.failed_detail.map((r) => (
                    <li key={`${r.line}-${r.email ?? ''}`} className="flex gap-3 px-3 py-2">
                      <span className="shrink-0 text-ink-400">Line {r.line}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-ink">{r.email || '(no email)'}</span>
                        <span className="block text-ink-500">{r.error}</span>
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-[12px] text-ink-400">
                  {done
                    ? 'These were skipped. Fix them in the file and import it again — rows already recorded will not be duplicated.'
                    : 'You can import the rest now and fix these separately, or correct the file and choose it again.'}
                </p>
              </div>
            )}

            {!done && preview.created > 0 && (
              <p className="text-[13px] text-ink-500">
                Nothing has been saved yet.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default AttendanceImportDialog
